import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { Button, Chip } from '@material-ui/core';
import {
  ChevronLeft,
  LucideIcon as Icon,
  HelpCircle as IconHowTo
} from 'lucide-react';
import React from 'react';
import { Link } from 'react-router-dom';
import { useDialogState } from '../hooks/useDialogState';
import { useTranslation } from '../i18next';
import { useRoutes } from '../routes';
import { useTheme } from '../themes';
import { COLORS } from '../themes/colors';

export const SIDE_NAV_WIDTH = 168;

const SIDE_NAV_COLORS = {
  groupLabel: '#6d6d6d',
  howTo: '#bdbdbd'
};

export type SideNavItem = {
  label: string;
  path: string;
  badge?: 'BETA' | 'NEW';
  count?: {
    color: string;
    backgroundColor: string;
    n: number;
  };
  children?: SideNavItem[];
  highlight?: boolean;
};

export type SideNavAction = {
  icon: Icon;
  label: string;
  onClick: () => void;
  // maybe allow other actions through a split button at some point
};

export type SideNavHowTo = React.ComponentType<{
  open: boolean;
  onClose: () => void;
}>;

export type SideNavBack = {
  path: string;
};

export type SideNavProps = {
  items?: SideNavItem[];
  action?: SideNavAction;
  howTo?: SideNavHowTo;
  back?: SideNavBack;
};

const DISTANCE_TO_TOP_BAR = 3;

const SideNavContainer = styled('div')`
  width: ${SIDE_NAV_WIDTH}px;
  top: ${(p) => p.theme.spacing(7 + DISTANCE_TO_TOP_BAR)}px;
  position: fixed;
  padding-right: ${(p) => p.theme.spacing(2)}px;
  margin-top: ${(p) => p.theme.spacing(3)}px;
`;

// eslint-disable-next-line
const GroupHeader = styled('div')`
  display: flex;
  align-items: center;
  margin-bottom: ${(p) => p.theme.spacing(1)}px;
  gap: ${(p) => p.theme.spacing(0.5)}px;
`;

// eslint-disable-next-line
const GroupLabel = styled('div')`
  text-transform: uppercase;
  color: ${SIDE_NAV_COLORS.groupLabel};
  font-weight: ${(p) => p.theme.typography.fontWeightBold};
`;

const newItemHighlight = keyframes`
  from { background-color: rgba(255, 220, 0, 0.35); }
  to { background-color: transparent; }
`;

const Item = styled('div')<{ selected: boolean; highlighted?: boolean }>((p) => ({
  fontSize: p.theme.custom.fontSize.m,
  marginBottom: p.theme.spacing(2),
  cursor: 'pointer',
  width: 'fit-content',
  borderBottom: '3px solid transparent',
  borderColor: p.selected ? `${p.theme.custom.colors.green.w400}` : 'none',
  fontWeight: p.selected ? p.theme.typography.fontWeightBold : undefined,
  animation: p.highlighted ? `${newItemHighlight} 2s ease-out forwards` : undefined
}));

const SideNavSection = styled('div')`
  margin-bottom: ${(p) => p.theme.spacing(3)}px;
`;

const SideNavHowToContainer = styled('div')`
  color: ${SIDE_NAV_COLORS.howTo};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${(p) => p.theme.spacing(0.5)}px;
`;

const BackContainer = styled('div')`
  display: flex;
  align-items: center;
  font-weight: ${(p) => p.theme.typography.fontWeightBold};
  gap: ${(p) => p.theme.spacing(0.5)}px;
`;

// eslint-disable-next-line
const Badge = ({ type }: { type?: SideNavItem['badge'] }) => {
  const theme = useTheme();

  const toStyle = (
    color: string,
    backgroundColor: string
  ): React.CSSProperties => ({
    color,
    backgroundColor,
    fontSize: 10,
    fontWeight: theme.typography.fontWeightBold,
    height: 18,
    textTransform: 'uppercase'
  });

  if (type === 'BETA') {
    return (
      <Chip
        size="small"
        label="New"
        style={toStyle(COLORS.blue.blue9, COLORS.blue.blue3)}
      />
    );
  }
  if (type === 'NEW') {
    return (
      <Chip
        size="small"
        label="New"
        style={toStyle(COLORS.orange.orange9, COLORS.orange.orange3)}
      />
    );
  }
  return null;
};

const CountContainer = styled('div')<{
  color: string;
  backgroundColor: string;
}>`
  color: ${(p) => p.color};
  background: ${(p) => p.backgroundColor};
  border-radius: 4px;
  padding: 2px 6px;
  font-weight: ${(p) => p.theme.typography.fontWeightBold};
`;

// eslint-disable-next-line
const Count = ({ d }: { d?: SideNavItem['count'] }) => {
  if (!d) {
    return null;
  }
  const { color, backgroundColor, n } = d;
  return (
    <CountContainer color={color} backgroundColor={backgroundColor}>
      {n}
    </CountContainer>
  );
};

const SideNav = ({ items, action, howTo, back }: SideNavProps) => {
  const { getPath } = useRoutes();
  const { t } = useTranslation();
  const currentPath = getPath();
  const { dialogOpen, openDialog, closeDialog } = useDialogState();
  return (
    <SideNavContainer>
      {back && (
        <SideNavSection>
          <Link to={back.path}>
            <BackContainer>
              <ChevronLeft />
              <div>{t('Back')}</div>
            </BackContainer>
          </Link>
        </SideNavSection>
      )}

      {items &&
        items.map((x) => (
          <React.Fragment key={x.label}>
            <Link to={x.path}>
              <Item selected={x.path.indexOf(currentPath) === 0 && !x.children?.some(c => c.path.indexOf(currentPath) === 0)}>
                {x.label}
              </Item>
            </Link>
            {x.children &&
              x.children.map((child, i) => (
                <Link key={child.label} to={child.path}>
                  <Item
                    selected={child.path.indexOf(currentPath) === 0}
                    highlighted={child.highlight}
                    style={{
                      marginLeft: 12,
                      marginBottom: i === x.children!.length - 1 ? 16 : 8,
                      fontSize: '0.85em',
                      lineHeight: 1.1
                    }}
                  >
                    <span style={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}>
                      {child.label}
                    </span>
                  </Item>
                </Link>
              ))}
          </React.Fragment>
        ))}
      {action && (
        <SideNavSection>
          <Button
            startIcon={<action.icon size={18} />}
            onClick={action.onClick}
            variant="contained"
            color="primary"
          >
            {action.label}
          </Button>
        </SideNavSection>
      )}
      {howTo && (
        <>
          <SideNavSection>
            <SideNavHowToContainer role="button" onClick={openDialog}>
              <IconHowTo size={16} />
              <div>How to use</div>
            </SideNavHowToContainer>

            {React.createElement(howTo, {
              open: dialogOpen,
              onClose: closeDialog
            })}
          </SideNavSection>
        </>
      )}
    </SideNavContainer>
  );
};

const PageBodyWrapper = styled('div')<{
  noTopPadding?: boolean;
  noSidePadding?: boolean;
}>`
  flex: 1;
  max-width: 100vw;
  display: flex;
  flex-direction: column;
  padding-top: ${(p) =>
    p.noSidePadding ? 0 : p.theme.spacing(DISTANCE_TO_TOP_BAR)}px;
  padding-right: ${(p) => (p.noSidePadding ? 0 : p.theme.spacing(2))}px;
  padding-bottom: ${(p) => (p.noSidePadding ? 0 : p.theme.spacing(6))}px;
  padding-left: ${(p) => (p.noSidePadding ? 0 : p.theme.spacing(2))}px;

  @media (min-width: 600px) {
    padding-left: ${(p) => (p.noSidePadding ? 0 : p.theme.spacing(4))}px;
    padding-right: ${(p) => (p.noSidePadding ? 0 : p.theme.spacing(4))}px;
  }

  @media (min-width: 1600px) {
    padding-left: ${(p) => (p.noSidePadding ? 0 : p.theme.spacing(6))}px;
    padding-right: ${(p) => (p.noSidePadding ? 0 : p.theme.spacing(6))}px;
  }
`;

const Main = styled('div')`
  width: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const MainWithSideNav = styled(Main)`
  padding-left: ${SIDE_NAV_WIDTH}px;
`;

export const PageBody: React.FC<{
  sideNav?: SideNavProps;
  noTopPadding?: boolean;
  noSidePadding?: boolean;
}> = ({ sideNav, noTopPadding, noSidePadding, children }) => {
  if (sideNav) {
    return (
      <PageBodyWrapper
        noTopPadding={noTopPadding}
        noSidePadding={noSidePadding}
      >
        <SideNav {...sideNav} />
        <MainWithSideNav>{children}</MainWithSideNav>
      </PageBodyWrapper>
    );
  }
  return (
    <PageBodyWrapper noTopPadding={noTopPadding} noSidePadding={noSidePadding}>
      <Main>{children}</Main>
    </PageBodyWrapper>
  );
};
